// @flow
import React, { Component } from "react";
import loadImage from "blueimp-load-image";
import classNames from "classnames";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Hidden from "@material-ui/core/Hidden";
import Button from "@material-ui/core/Button";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import PhotoAlbumIcon from "@material-ui/icons/PhotoAlbum";
import { withStyles } from "@material-ui/core/styles";

import type { DictionaryType } from "../../../global/types/GeneralTypes";
import type { ButtonStylesClassesType } from "../../../global/styles/buttonStyles";
import type { IconStylesClassesType } from "../../../global/styles/iconStyles";
import type { PanelStylesClassesType } from "../../../global/styles/generalStyles";
import { buttonStyles } from "../../../global/styles/buttonStyles";
import { iconStyles } from "../../../global/styles/iconStyles";
import { formText, panelStyles } from "../../../global/styles/generalStyles";
import {
  WebCamera,
  LoadingProgress,
  DraggableUploadButton
} from "imagus-components";
import SampleImages from "./SampleImages";

const styles = (theme: DictionaryType): DictionaryType => ({
  ...panelStyles(theme),
  ...buttonStyles(theme),
  ...iconStyles(theme),
  helpText: {
    color: theme.palette.primary.main,
    width: "100%",
    textAlign: "left",
    fontSize: "0.875rem",
    padding: "5px"
  },
  rightMargin: {
    marginRight: "5px"
  }
});

type FaceUploadLayoutStatesType = {
  loading: boolean,
  isCamera: boolean,
  isSample: boolean
};

type FaceUploadLayoutClassesType = {
  ...ButtonStylesClassesType,
  ...IconStylesClassesType,
  ...PanelStylesClassesType,
  formText: DictionaryType,
  helpText: DictionaryType,
  rightMargin: DictionaryType
};

type FaceUploadLayoutPropsType = {
  classes: FaceUploadLayoutClassesType,
  faceSearch: { [key: string]: * },
  setSearchToken: Function,
  setLoadedImage: Function,
  uploadFile: Function,
  setImageError: Function,
  setStep: Function,
  samples: Array<string>
};

class FaceUploadLayout extends Component<
  FaceUploadLayoutPropsType,
  FaceUploadLayoutStatesType
> {
  constructor(props: FaceUploadLayoutPropsType) {
    super(props);
    this.state = {
      loading: false,
      isCamera: false,
      isSample: false
    };
  }

  componentDidMount() {
    if (this.props.faceSearch.imgToken !== null) {
      this.props.setSearchToken(null);
    }
    if (this.props.faceSearch.loadedImage !== null) {
      this.props.setLoadedImage(null);
    }
    this.resetForm();
  }

  uploadFileHandler = (e: *): * => {
    this.setState({ loading: true });
    this.props.setImageError("");
    let file;
    if (e.dataTransfer) {
      file = e.dataTransfer.files[0];
    } else if (e.target) {
      file = e.target.files[0];
    }
    if (file) {
      this.props.uploadFile(
        file,
        this.completCall.bind(this, file),
        this.uploadFailCall.bind(this)
      );
    } else {
      this.props.setImageError("Not Supported Image Format");
      this.setState({ loading: false });
    }
  };

  captureCamera = async (data: string): * => {
    this.setState({ loading: true });
    this.props.setImageError("");
    let file;
    fetch(data)
      .then((res: Response): Promise<Blob> => res.blob())
      .then((blob: Blob) => {
        // Edge not support File Construct
        file = new Blob([blob], { type: "image/jpeg" });
        this.props.uploadFile(
          file,
          this.completCall.bind(this, file),
          this.uploadFailCall.bind(this)
        );
      });
  };

  loadSample = async (imgUrl: string): * => {
    this.setState({ loading: true });
    this.props.setImageError("");
    let file;
    fetch(imgUrl)
      .then((res: Response): Promise<Blob> => res.blob())
      .then((blob: Blob) => {
        // Edge not support File Construct
        file = new Blob([blob], { type: "image/jpeg" });
        this.props.uploadFile(
          file,
          this.completCall.bind(this, file),
          this.uploadFailCall.bind(this)
        );
      });
  };

  completCall = (file: File | Blob): * => {
    const loadingImage = loadImage(file, this.uploadDoneCall, {
      orientation: true,
      canvas:true
    });
  };

  uploadDoneCall = (canvas: HTMLCanvasElement): * => {
    const img = canvas.toDataURL("image/jpeg");
    this.props.setLoadedImage(img);
    this.setState({ isCamera: false, loading: false, isSample: false });
    this.props.setStep(1);
  };

  uploadFailCall(msg: string) {
    this.props.setImageError(msg);
    this.setState({ loading: false });
  }

  resetForm = (): * => {
    this.props.setImageError("");
    this.setState({ isCamera: false, loading: false, isSample: false });
    this.props.setStep(0);
  };

  switch2Camera = (): * => {
    this.setState({ isCamera: true });
  };

  switch2Samples = (): * => {
    this.setState({ isSample: true });
  };

  render(): * {
    const classes = this.props.classes;

    let content: React$Element<*>;
    if (this.state.loading) {
      content = (
        <div className={classes.formText}>
          <LoadingProgress />
        </div>
      );
    } else if (this.state.isCamera) {
      content = (
        <WebCamera
          captureCamera={this.captureCamera}
          cancelCamera={this.resetForm}
          setError={(): Function =>
            this.props.setImageError("Can NOT open your web camera.")
          }
        />
      );
    } else if (this.state.isSample) {
      content = (
        <SampleImages
          cancelSamples={this.resetForm}
          loadSample={this.loadSample}
          samples={this.props.samples}
        />
      );
    } else {
      content = (
        <div>
          <DraggableUploadButton uploadFileActions={this.uploadFileHandler} />
          <Typography variant="caption" className={classes.helpText}>
            *Supported Image Formats: PNG | JPG | BMP | WEBP
            <br />
            *Image File Size: less than 10 MB
          </Typography>
          <Hidden smDown>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              onClick={this.switch2Camera}
              className={classNames(classes.darkBtn, classes.rightMargin)}
            >
              <PhotoCameraIcon
                className={classNames(classes.lightIcon, classes.rightMargin)}
              />
              Web Camera
            </Button>
          </Hidden>
          <Button
            color="primary"
            variant="contained"
            type="submit"
            onClick={this.switch2Samples}
            className={classes.darkBtn}
          >
            <PhotoAlbumIcon
              className={classNames(classes.lightIcon, classes.rightMargin)}
            />
            Sample Images
          </Button>
        </div>
      );
    }

    return (
      <div className={classes.appPanelContent}>
        <Grid container spacing={8}>
          <Grid item xs={12} md={5}>
            {content}
          </Grid>
        </Grid>
      </div>
    );
  }
}

export default withStyles(styles)(FaceUploadLayout);
